import type { Agent, Status } from '@studio/models/agent';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { BootAccess, BootRoomFacts } from '#~/depot/types.js';
import type { Room } from '@studio/models/room';

/** Room boot-readiness flags consumed by the room route's render branch. */
export type RoomBootReadiness = {
  readonly isReady: boolean;
  readonly showOfflinePlaceholder: boolean;
  readonly showPage: boolean;
};

// bridge-less Studio boot: a Studio with no Bridge linked enumerates zero agents. Both
// `showOfflinePlaceholder` (requires length > 0) and `showPage` (requires a
// non-offline agent) were false for an empty agent set, so boot-readiness
// dead-spotted at isReady=false forever and the BootOverlay never cleared
// ("Loading your studio…"). <AgentProvider> only mounts the room subtree AFTER
// useRoomAgentStatus resolves (provider.tsx gates children behind isReady), so
// zero agents here means enumeration genuinely returned none — the Studio is
// bridge-less. Treat a settled-empty enumeration as ready and route it to the
// same <Placeholder>, which already renders <Linker/> ("link a Bridge") when
// agents.length === 0. A short grace window absorbs cold-boot enumeration lag so
// the empty state never flashes before a real Bridge's status lands.
// Per rule: reference-rules-in-comments (ai.knowledge/global).
export const EMPTY_STUDIO_GRACE_MS = 4_000;

/**
 * Start the bridge-less grace period once the room's agent enumeration has
 * settled at zero agents and the machine-identity read is not still pending.
 *
 * History (hive-enablement boot hang, 2026-08-07 QA): this gate originally
 * ALSO required `machineAgentResolution === 'loaded' && !machineAgentId`,
 * which deterministically wedged boot on "Loading your studio…" in two ways:
 * (1) an errored identity read ('error' ≠ 'loaded') held the gate closed
 * forever — violating `absent-data-gates-permissively` (errored reads must
 * degrade, not enforce); (2) a machine linked to a DIFFERENT studio
 * (`machineAgentId` defined) could never dismiss the overlay on a
 * legitimately empty studio it navigated to (QA: create-new-studio hang).
 *
 * The transient-refresh flash that gate was protecting against is now closed
 * UPSTREAM in `useRoomAgentStatus` (agent/provider/utils.ts): a FAILED read
 * never settles as "zero agents" (strict enumeration), and a SUCCEEDED read
 * of zero agents while this machine's identity is linked is re-read for a
 * bounded budget before it may settle (`LINKED_EMPTY_ENUMERATION_BUDGET` —
 * the 2026-08 Windows rapid-refresh regression, where a server-side blank of
 * the agent↔room association "succeeded" with zero agents and dismissed the
 * overlay onto the Linker). So `statuses` can only be a genuinely-settled
 * answer by the time this gate runs. Only `'unknown'` (identity read still
 * in flight) holds the grace back — and the identity store's session ceiling
 * guarantees that state is bounded.
 * Per rule: absent-data-gates-permissively + reference-rules-in-comments
 * (ai.knowledge/global).
 */
export const shouldArmEmptyStudioGrace = ({
  hasRoomData,
  statuses,
  machineAgentResolution
}: {
  readonly hasRoomData: boolean;
  readonly statuses: readonly Status[];
  readonly machineAgentId?: Agent['id'] | undefined;
  readonly machineAgentResolution: 'unknown' | 'loaded' | 'error';
}): boolean => hasRoomData && statuses.length === 0 && machineAgentResolution !== 'unknown';

/**
 * Pure boot-readiness decision. Extracted for unit-testability, mirroring the
 * pure helpers in `boot-stall.ts` / `auth/room-selection.ts`.
 *
 * @param input - Room-data presence, agent statuses, and whether the
 *   bridge-less grace window has elapsed.
 * @returns Room boot-readiness flags.
 */
export const computeRoomBootReadiness = ({
  hasRoomData,
  statuses,
  bridgelessGraceElapsed
}: {
  readonly hasRoomData: boolean;
  readonly statuses: readonly Status[];
  readonly bridgelessGraceElapsed: boolean;
}): RoomBootReadiness => {
  const hasNonOfflineAgent = statuses.some((s) => s !== 'offline');
  const showPage = hasNonOfflineAgent && hasRoomData;

  // Bridge-less: room loaded, enumeration settled with zero agents, and the
  // grace window has elapsed (so cold-boot lag can't flash the empty state).
  const showEmptyStudio = hasRoomData && statuses.length === 0 && bridgelessGraceElapsed;

  // Fold the empty-Studio case into the offline-placeholder branch: the room
  // route renders <Placeholder> for it, which shows <Linker/> for zero agents.
  const showOfflinePlaceholder =
    (statuses.length > 0 && statuses.every((s) => s === 'offline')) || showEmptyStudio;

  return {
    isReady: showOfflinePlaceholder || showPage,
    showOfflinePlaceholder,
    showPage
  };
};

/** Pref key page.tsx caches `canControl` under; readiness reads the same key. */
export const roomCanControlCacheKey = (roomId: Room['id']): string =>
  `room.${roomId}.cache.permissions.canControl`;

const WRITER_ROLES = new Set<AgentRegistryMembershipRole>([
  AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER,
  AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN,
  AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_PRODUCER
]);

/**
 * Typed membership only. Unknown, unspecified, or a non-enum value stays
 * unknown — never inferred as writer.
 */
export const bootAccessFromRole = (role: unknown): BootAccess => {
  if (role === AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_VIEWER) {
    return 'viewer';
  }
  if (typeof role === 'number' && WRITER_ROLES.has(role)) {
    return 'writer';
  }
  return 'unknown';
};

/**
 * Access as the room page actually renders it. A typed role wins. Without one
 * the page still renders on the `canControl` it has — the record's, or the
 * value cached from the last visit — so readiness follows that same source
 * instead of holding a rendered Studio at `unknown` until the launch budget
 * expires and tears it down. No boolean means the page shows its Loader: the
 * attempt is genuinely not ready.
 * Per rule: absent-data-gates-permissively (ai.knowledge/global).
 */
export const bootAccessFrom = ({
  role,
  renderedCanControl
}: {
  role: unknown;
  renderedCanControl: boolean | undefined;
}): BootAccess => {
  const typed = bootAccessFromRole(role);
  if (typed !== 'unknown' || renderedCanControl === undefined) return typed;
  return renderedCanControl ? 'writer' : 'viewer';
};

/**
 * Has the room publisher actually observed this room yet? The previous
 * enumeration and inventory helpers computed empty/populated classes that
 * readiness never distinguished — it only rejected `unknown` — and the saved
 * identity set behind inventory (including its transient filtering) therefore
 * changed no decision. One marker replaces both. A page that renders on a
 * known `canControl` counts as observed even while its registry record lags.
 */
export const bootRoomFactsFrom = ({
  recordPresent,
  rendered = false,
  ready,
  roomPresent
}: {
  recordPresent: boolean;
  /** The page renders (no Loader) on a known `canControl`, record or cached. */
  rendered?: boolean;
  ready: boolean;
  roomPresent: boolean;
}): BootRoomFacts => ((recordPresent || rendered) && ready && roomPresent ? 'observed' : 'unknown');
